import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface JobGrade {
  id: string;
  tenant_id: string;
  name: string;
  level: number;
  min_salary: number | null;
  max_salary: number | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobPosition {
  id: string;
  tenant_id: string;
  title: string;
  department: string | null;
  grade_id: string | null;
  headcount: number;
  is_open: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateJobGradeInput {
  name: string;
  level: number;
  min_salary?: number | null;
  max_salary?: number | null;
  description?: string | null;
}

export interface UpdateJobGradeInput {
  id: string;
  name?: string;
  level?: number;
  min_salary?: number | null;
  max_salary?: number | null;
  description?: string | null;
}

export interface CreateJobPositionInput {
  title: string;
  department?: string | null;
  grade_id?: string | null;
  headcount?: number;
  is_open?: boolean;
  description?: string | null;
}

export interface UpdateJobPositionInput {
  id: string;
  title?: string;
  department?: string | null;
  grade_id?: string | null;
  headcount?: number;
  is_open?: boolean;
  description?: string | null;
}

export interface JobPositionFilters {
  is_open?: boolean;
}

export const jobGradesKeys = {
  all: ['job-grades'] as const,
  grades: (tenantId: string) => [...jobGradesKeys.all, 'grades', tenantId] as const,
  positions: (tenantId: string, filters?: JobPositionFilters) =>
    [...jobGradesKeys.all, 'positions', tenantId, filters] as const,
} as const;

export function useJobGrades(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: jobGradesKeys.grades(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_grades')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('level', { ascending: true });
      if (error) throw error;
      return data as JobGrade[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateJobGrade(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateJobGradeInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_grades')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as JobGrade;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.grades(tenantId) }),
  });
}

export function useUpdateJobGrade(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateJobGradeInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_grades')
        // @ts-ignore dynamic
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as JobGrade;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.grades(tenantId) }),
  });
}

export function useDeleteJobGrade(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_grades')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.grades(tenantId) }),
  });
}

export function useJobPositions(
  supabase: SupabaseClientLike,
  tenantId: string,
  filters?: JobPositionFilters,
) {
  return useQuery({
    queryKey: jobGradesKeys.positions(tenantId, filters),
    queryFn: async () => {
      const builder = (supabase as unknown as { from: (t: string) => any })
        .from('job_positions')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('title', { ascending: true });

      if (filters?.is_open !== undefined) {
        // @ts-ignore dynamic
        builder.eq('is_open', filters.is_open);
      }

      // @ts-ignore dynamic
      const { data, error } = await builder;
      if (error) throw error;
      return data as JobPosition[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateJobPosition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateJobPositionInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_positions')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as JobPosition;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.positions(tenantId) }),
  });
}

export function useUpdateJobPosition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateJobPositionInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_positions')
        // @ts-ignore dynamic
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as JobPosition;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.positions(tenantId) }),
  });
}

export function useDeleteJobPosition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('job_positions')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: jobGradesKeys.positions(tenantId) }),
  });
}
